import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  AmountType,
  BaseBotData,
  BotData,
  BotType,
  LongShort,
  MarginTypes,
  StrippedBotData,
  SubGroup,
  LanguageNameSpaces,
} from "@shared/types";
import {
  BotFormType,
  Error,
  PercentageBaseType,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { isNumber, toNumber } from "lodash";
import {
  getAmountTypeSuffix,
  getIsPercentAmountType,
} from "@shared/features/bots/utils";
import { getBotAmountTypeAndValue } from "@shared/features/bots/BotForm/common/utils";
import { useContext, useEffect, useState } from "react";
import { isSymbol } from "@shared/types/utils";
import {
  useAmountError,
  useBotFormDefaultAmount,
} from "@shared/features/bots/BotForm/common/hooks";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import { useClientsData, useUser } from "@shared/utils/hooks/misc";
import { AMOUNT_PERCENTAGE_BASE_FEATURE_FLAG } from "@shared/common/constants";
import { PERCENTAGE_AMOUNT_OPTIONS } from "@shared/features/bots/BotForm/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface UseAmountSelectorProps {
  isCopyPublicBotSummary?: boolean;
  isOnboarding?: boolean;
  isBacktestModal?: boolean;
}

const useAmountSelector = ({
  isCopyPublicBotSummary,
  isOnboarding,
  isBacktestModal,
}: UseAmountSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    isEdit,
    botType,
    botGeneralData,
    amountType,
    setAmountType,
    amountValue,
    setAmountValue,
    parsedEntries,
    botAmountData,
    getBotAmountDataStatus,
    symbols,
    account,
    exchanges,
    isPublic,
    multiplier,
    marginType,
    longShort,
    botFormType,
    fieldsToInitialize,
    removeFieldToInitialize,
    subGroup,
    subGroupBotConfiguration,
    isBacktesting,
    amountChangedRef,
    isChannelConfiguration,
    checkedFields,
    addError,
    removeError,
    isAdvancedView,
    amountPercentageBaseType,
    setAmountPercentageBaseType,
    amountCustomBaseValue,
    setAmountCustomBaseValue,
    useSymbolFromAlert,
  } = useContext(BotFormContext)!;
  const { activeClientsData, getClientsListStatus } = useClientsData();
  const { getHelperData } = useFormHelperData();
  const symbol = isSymbol(symbols) ? undefined : symbols?.[0];
  const exchange = isSymbol(account) ? undefined : account?.exchange;
  const [localAmountValue, setLocalAmountValue] = useState<string | undefined>(
    isSymbol(amountValue) ? undefined : amountValue?.toString()
  );
  const [initializedFormData, setInitializedFormData] = useState<
    | {
        exchange: string;
        symbol: string;
        multiplier?: number;
        marginType?: MarginTypes;
        longShort?: LongShort[];
        minBotAmount?: number;
        maxBotAmount?: number;
        subGroup?: SubGroup;
        subGroupBotConfiguration?: BotData | StrippedBotData;
      }
    | undefined
  >();
  const isGridBot = botType === BotType.GRID;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const shouldSetLocalValue = fieldsToInitialize?.has("amountValue");
  const {
    getDefaultAmountValue,
    amountPrecision,
    isFetching: isFetchingBotAmountData,
    mostTradedCoin,
  } = useBotFormDefaultAmount();
  const {
    amountError: minAmountErrorMessage,
    minBotAmount,
    maxBotAmount,
    isWarning,
    isError,
    baselineErrorData,
  } = useAmountError(
    isOnboarding,
    isCopyPublicBotSummary,
    mostTradedCoin,
    isBacktestModal
  );
  const isPercentAmount = getIsPercentAmountType(amountType);
  const { user } = useUser();
  const hasAmountBaselineFeatureFlag = !!user?.hasFeatureFlag(
    AMOUNT_PERCENTAGE_BASE_FEATURE_FLAG
  );
  const showAmountBaseline =
    isSignalsBot &&
    !isChannelConfiguration &&
    isAdvancedView &&
    isPercentAmount &&
    hasAmountBaselineFeatureFlag;
  const displayCustomAmountBase =
    showAmountBaseline &&
    amountPercentageBaseType === PercentageBaseType.CUSTOM_AMOUNT;

  const isToggledOn =
    (!isChannelConfiguration && !isTradingViewSignalsBot) ||
    checkedFields.has(ToggleField.AMOUNT_PER_TRADE);

  useEffect(() => {
    if (!isChannelConfiguration) return;
    if (isToggledOn && isError) {
      addError(Error.GENERAL_AMOUNT);
    } else {
      removeError(Error.GENERAL_AMOUNT);
    }
  }, [isError, isToggledOn]);

  useEffect(() => {
    if (amountValue && amountValue.toString() !== localAmountValue) {
      setLocalAmountValue(
        isSymbol(amountValue) ? undefined : amountValue?.toString()
      );
    }
  }, [amountValue]);

  useEffect(() => {
    if (shouldSetLocalValue) {
      setLocalAmountValue(
        isSymbol(amountValue) ? undefined : amountValue?.toString()
      );
      removeFieldToInitialize?.("amountValue");
    }
  }, [shouldSetLocalValue]);

  const amountTypeRawData = botGeneralData?.options["Amount Type"];
  let amountTypeOptions = amountTypeRawData?.[0] || [];
  if (isOnboarding) {
    amountTypeOptions = amountTypeOptions.filter(
      (option) => option.value !== AmountType.PERCENTAGE
    );
  } else if (isChannelConfiguration || isTradingViewSignalsBot) {
    amountTypeOptions = [...PERCENTAGE_AMOUNT_OPTIONS];
  }

  const handleAmountTypeChange = (amountType: AmountType) => {
    setAmountType(amountType);
    const isPercentType = getIsPercentAmountType(amountType);
    if (!isPercentType) {
      setAmountPercentageBaseType(undefined);
      setAmountCustomBaseValue(undefined);
    } else if (!amountPercentageBaseType) {
      setAmountPercentageBaseType(PercentageBaseType.PAIR_AMOUNT);
      setAmountCustomBaseValue(undefined);
    }
  };

  const handleAmountChange = (amount?: number | string) => {
    let numberAmount: number;
    let stringAmount: string;
    if (typeof amount === "string") {
      numberAmount = toNumber(amount);
      stringAmount = amount;
    } else {
      numberAmount = amount || 0;
      stringAmount = amount?.toString() || "";
    }
    setAmountValue(numberAmount);
    setLocalAmountValue(stringAmount);
  };

  const setEditBotAmount = (
    subGroupBotConfiguration?: BotData | StrippedBotData
  ) => {
    if (!botGeneralData && !subGroupBotConfiguration) return false;
    let initialized = false;
    const initializationBotData = (subGroupBotConfiguration ??
      botGeneralData) as BaseBotData | BotData;
    const {
      amountType,
      amountValue,
      amountPercentageBaseType,
      amountCustomBaseValue,
    } = getBotAmountTypeAndValue(
      initializationBotData,
      hasAmountBaselineFeatureFlag
    );
    if (amountType && amountValue) {
      handleAmountTypeChange(amountType);
      handleAmountChange(amountValue);
      setAmountPercentageBaseType(amountPercentageBaseType);
      setAmountCustomBaseValue(amountCustomBaseValue);
      initialized = true;
    }
    return initialized;
  };

  const shouldInitializeAmount = () => {
    const formInitialized =
      (isSignalsBot || !!parsedEntries) &&
      !!botGeneralData &&
      botAmountData?.initialized &&
      !!account &&
      !!symbol &&
      !getBotAmountDataStatus.isFetching &&
      !isFetchingBotAmountData;
    const isValidBotFormType =
      botFormType !== BotFormType.BULK_EDIT &&
      (!initializedFormData ||
        botFormType === BotFormType.CREATE ||
        botFormType === BotFormType.COPY_PUBLIC ||
        (isSignalsBot &&
          (botFormType === BotFormType.EDIT ||
            botFormType === BotFormType.DUPLICATE)));
    const isValidBotType =
      !isGridBot || botFormType === BotFormType.COPY_PUBLIC;
    const formValuesChanged =
      isValidBotFormType &&
      !useSymbolFromAlert &&
      (symbol !== initializedFormData?.symbol ||
        exchange !== initializedFormData?.exchange ||
        multiplier !== initializedFormData?.multiplier ||
        longShort !== initializedFormData?.longShort ||
        marginType !== initializedFormData?.marginType ||
        minBotAmount !== initializedFormData?.minBotAmount ||
        maxBotAmount !== initializedFormData?.maxBotAmount ||
        subGroup?.id !== initializedFormData?.subGroup?.id ||
        subGroupBotConfiguration !==
          initializedFormData?.subGroupBotConfiguration);
    return (
      !isBacktestModal &&
      !isBacktesting &&
      isValidBotType &&
      !amountChangedRef.current &&
      isValidBotFormType &&
      formInitialized &&
      (amountType === undefined ||
        amountValue === undefined ||
        formValuesChanged)
    );
  };

  useEffect(() => {
    if (
      getClientsListStatus.isSuccess &&
      !activeClientsData?.length &&
      !amountType &&
      !amountValue &&
      !isTradingViewSignalsBot
    ) {
      handleAmountTypeChange(AmountType.FIXED_USD_AMOUNT);
      setAmountValue(1000);
    }
  }, [getClientsListStatus.isSuccess, activeClientsData?.length]);

  useEffect(() => {
    if (!shouldInitializeAmount()) return;
    let initializedValue = false;
    if ((isEdit && !isPublic) || subGroupBotConfiguration) {
      initializedValue = setEditBotAmount(subGroupBotConfiguration);
    } else {
      const defaultAmountType = AmountType.FIXED_USD_AMOUNT;
      handleAmountTypeChange(defaultAmountType);
      const defaultAmount =
        getDefaultAmountValue(defaultAmountType)?.defaultAmountValue;
      if (isNumber(defaultAmount)) {
        handleAmountChange(defaultAmount);
        initializedValue = true;
      }
    }
    if (
      initializedValue &&
      !isSymbol(multiplier) &&
      !isSymbol(marginType) &&
      !isSymbol(longShort) &&
      symbol
    ) {
      setInitializedFormData({
        exchange: exchange!,
        symbol,
        multiplier,
        marginType,
        longShort,
        minBotAmount,
        maxBotAmount,
        subGroup,
        subGroupBotConfiguration,
      });
    }
  }, [
    isBacktesting,
    parsedEntries,
    amountChangedRef.current,
    botGeneralData,
    botAmountData?.initialized,
    initializedFormData,
    minBotAmount,
    maxBotAmount,
    multiplier,
    marginType,
    longShort,
    symbol,
    exchange,
    exchanges,
    subGroup,
    subGroupBotConfiguration,
    isFetchingBotAmountData,
  ]);

  const isAmountTypeMultiValue = isSymbol(amountType);
  const isAmountValueMultiValue = isSymbol(amountValue);

  const amountValueData = {
    value: isAmountValueMultiValue ? "" : localAmountValue,
    suffix: isAmountValueMultiValue
      ? undefined
      : getAmountTypeSuffix(amountType),
    precision: amountPrecision,
    onChange: (value: string) => {
      amountChangedRef.current = true;
      handleAmountChange(value);
    },
    placeholder: isAmountValueMultiValue ? t("labels.multi") : "0",
    bordered: false,
  };

  const amountTypeData = {
    value: isAmountTypeMultiValue ? undefined : amountType,
    placeholder: isAmountTypeMultiValue ? t("labels.multi") : undefined,
    onChange: (value: any) => {
      amountChangedRef.current = true;
      handleAmountTypeChange(value);
      handleAmountChange(getDefaultAmountValue(value)?.defaultAmountValue);
    },
    options: amountTypeOptions,
    title: "Amount Type",
    bordered: false,
    warning:
      amountType === AmountType.RISK_PERCENTAGE
        ? "Please note that when using the Risk Percentage amount type, all Cross signals that don't have a stop-loss will be ignored. If you wish to change this behavior, consider adding a default stop to the bot's configuration."
        : undefined,
  };

  const amountPercentBaselineData = {
    shouldDisplay: showAmountBaseline,
    value: amountPercentageBaseType,
    onChange: (value: any) => {
      setAmountPercentageBaseType(value);
      if (value !== PercentageBaseType.CUSTOM_AMOUNT) {
        setAmountCustomBaseValue(undefined);
      }
    },
    options: AMOUNT_PERCENT_BASELINE_OPTIONS,
    title: "Amount Baseline",
    bordered: false,
  };

  const amountCustomBaseValueData = {
    shouldDisplay: displayCustomAmountBase,
    value: amountCustomBaseValue,
    suffix: getAmountTypeSuffix(AmountType.FIXED_USD_AMOUNT),
    precision: amountPrecision,
    onChange: setAmountCustomBaseValue,
    placeholder: "0",
    bordered: false,
  };

  return {
    minAmountErrorMessage,
    isWarning,
    isError,
    amountValueData,
    amountTypeData,
    valuesChanged: amountChangedRef.current,
    helperData: getHelperData("amountValue"),
    amountPercentBaselineData,
    amountCustomBaseValueData,
    baselineErrorData,
  };
};

export default useAmountSelector;

const AMOUNT_PERCENT_BASELINE_OPTIONS = [
  { value: PercentageBaseType.PAIR_AMOUNT, name: "Pair Amount" },
  { value: PercentageBaseType.CUSTOM_AMOUNT, name: "Custom Amount" },
];
